import React, { useState, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { formatCurrency, formatDate } from 'utils/lmmsHelpers';
import { useApprovalAuditReport, useDataChangeAuditTrail } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
import { exportStyledExcel, type ExportFilter } from 'utils/exportStyledExcel';
import type { ApprovalAuditReportEntry } from 'api/types/approval.types';
import type { AuditTrailEntry } from 'api/types/auditTrail.types';

// ============================================================================
// BADGE HELPERS
// ============================================================================

const getRecordTypeBadge = (type: string): React.CSSProperties => {
  const base: React.CSSProperties = {
    display: 'inline-block', padding: '2px 10px', borderRadius: '9999px',
    fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
  };
  switch (type) {
    case 'addon_request': return { ...base, background: '#dbeafe', color: '#1e40af' };
    case 'closing': return { ...base, background: '#fce4ec', color: '#b71c1c' };
    case 'combined': return { ...base, background: '#e0f2fe', color: '#0369a1' };
    case 'loan_mapping': return { ...base, background: '#f3e8ff', color: '#6b21a8' };
    default: return { ...base, background: '#f1f5f9', color: '#475569' };
  }
};

const getRecordTypeLabel = (type: string): string => {
  switch (type) {
    case 'addon_request': return 'Add-On';
    case 'closing': return 'Closing';
    case 'combined': return 'Combined';
    case 'loan_mapping': return 'Loan Mapping';
    default: return type;
  }
};

const getActionBadge = (action: string): React.CSSProperties => {
  const base: React.CSSProperties = {
    display: 'inline-block', padding: '2px 10px', borderRadius: '9999px',
    fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
  };
  const a = action?.toLowerCase();
  if (a === 'approved') return { ...base, background: '#dcfce7', color: '#166534' };
  if (a === 'returned') return { ...base, background: '#fef3c7', color: '#92400e' };
  if (a === 'rejected') return { ...base, background: '#fce4ec', color: '#b71c1c' };
  if (a === 'amended') return { ...base, background: '#f3e8ff', color: '#6b21a8' };
  return { ...base, background: '#f1f5f9', color: '#475569' };
};

const formatFieldName = (name: string): string =>
  name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ============================================================================
// MAIN SCREEN
// ============================================================================

const AuditTrailReportScreen: React.FC = () => {
  const { showMessage } = useSnackbar();

  // Tab state
  const [activeTab, setActiveTab] = useState<'approval' | 'data'>('approval');

  // API hooks
  const { data: approvalData, loading: approvalLoading, error: approvalError, refetch: refetchApproval } = useApprovalAuditReport();
  const { data: dataChangeData, loading: dataChangeLoading, error: dataChangeError, refetch: refetchDataChange } = useDataChangeAuditTrail();

  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [recordTypeFilter, setRecordTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ============================================================================
  // FILTERED DATA — APPROVAL HISTORY
  // ============================================================================

  const filteredApprovalData = useMemo(() => {
    return (approvalData || []).filter((row) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchable = `${row.referenceNumber} ${row.customerName || ''} ${row.actionedByName || ''} ${row.remark || ''} ${row.action} ${getRecordTypeLabel(row.recordType)}`.toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      if (recordTypeFilter && row.recordType !== recordTypeFilter) return false;
      if (actionFilter && row.action?.toLowerCase() !== actionFilter.toLowerCase()) return false;
      if (dateFrom) {
        const rowDate = row.actionedAt?.split('T')[0];
        if (rowDate && rowDate < dateFrom) return false;
      }
      if (dateTo) {
        const rowDate = row.actionedAt?.split('T')[0];
        if (rowDate && rowDate > dateTo) return false;
      }
      return true;
    });
  }, [approvalData, searchQuery, recordTypeFilter, actionFilter, dateFrom, dateTo]);

  // ============================================================================
  // FILTERED DATA — DATA CHANGES
  // ============================================================================

  const filteredDataChangeData = useMemo(() => {
    return (dataChangeData || []).filter((row) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchable = `${row.fieldName} ${row.oldValue || ''} ${row.newValue || ''} ${row.reason || ''} ${row.changedByName || ''} ${getRecordTypeLabel(row.recordType)}`.toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      if (recordTypeFilter && row.recordType !== recordTypeFilter) return false;
      if (dateFrom) {
        const rowDate = row.changedAt?.split('T')[0];
        if (rowDate && rowDate < dateFrom) return false;
      }
      if (dateTo) {
        const rowDate = row.changedAt?.split('T')[0];
        if (rowDate && rowDate > dateTo) return false;
      }
      return true;
    });
  }, [dataChangeData, searchQuery, recordTypeFilter, dateFrom, dateTo]);

  // ============================================================================
  // COLUMN DEFINITIONS — APPROVAL HISTORY
  // ============================================================================

  const approvalColumns: SyutGridColumn<ApprovalAuditReportEntry>[] = useMemo(() => [
    { id: 'referenceNumber', header: 'Reference #', accessorKey: 'referenceNumber', size: 160 },
    {
      id: 'recordType', header: 'Record Type', accessorKey: 'recordType', size: 120,
      cell: ({ getValue }: any) => <span style={getRecordTypeBadge(getValue())}>{getRecordTypeLabel(getValue())}</span>,
    },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 150 },
    {
      id: 'amount', header: 'Amount', accessorKey: 'amount', size: 140,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600 }}>{getValue() != null ? formatCurrency(getValue()) : '-'}</span>,
    },
    {
      id: 'action', header: 'Action', accessorKey: 'action', size: 120,
      cell: ({ getValue }: any) => {
        const val = getValue();
        return val ? <span style={getActionBadge(val)}>{val.toUpperCase()}</span> : '-';
      },
    },
    { id: 'actionedByName', header: 'Actioned By', accessorKey: 'actionedByName', size: 140 },
    {
      id: 'remark', header: 'Remark', accessorKey: 'remark', size: 250,
      cell: ({ getValue }: any) => (
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }} title={getValue() || ''}>
          {getValue() || '-'}
        </span>
      ),
    },
    {
      id: 'actionedAt', header: 'Actioned At', accessorKey: 'actionedAt', size: 150,
      cell: ({ getValue }: any) => getValue() ? formatDate(getValue()) : '-',
    },
  ], []);

  // ============================================================================
  // COLUMN DEFINITIONS — DATA CHANGES
  // ============================================================================

  const dataChangeColumns: SyutGridColumn<AuditTrailEntry>[] = useMemo(() => [
    {
      id: 'recordType', header: 'Record Type', accessorKey: 'recordType', size: 120,
      cell: ({ getValue }: any) => <span style={getRecordTypeBadge(getValue())}>{getRecordTypeLabel(getValue())}</span>,
    },
    {
      id: 'fieldName', header: 'Field', accessorKey: 'fieldName', size: 180,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 500 }}>{formatFieldName(getValue())}</span>,
    },
    {
      id: 'oldValue', header: 'Old Value', accessorKey: 'oldValue', size: 160,
      cell: ({ getValue }: any) => (
        <span style={{ color: 'var(--danger)', textDecoration: getValue() ? 'line-through' : 'none', fontSize: '0.85rem' }}>
          {getValue() || '-'}
        </span>
      ),
    },
    {
      id: 'newValue', header: 'New Value', accessorKey: 'newValue', size: 160,
      cell: ({ getValue }: any) => (
        <span style={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.85rem' }}>
          {getValue() || '-'}
        </span>
      ),
    },
    {
      id: 'reason', header: 'Reason', accessorKey: 'reason', size: 250,
      cell: ({ getValue }: any) => (
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }} title={getValue() || ''}>
          {getValue() || '-'}
        </span>
      ),
    },
    { id: 'changedByName', header: 'Changed By', accessorKey: 'changedByName', size: 140 },
    {
      id: 'changedAt', header: 'Changed At', accessorKey: 'changedAt', size: 150,
      cell: ({ getValue }: any) => getValue() ? formatDate(getValue()) : '-',
    },
  ], []);

  // ============================================================================
  // EXPORT
  // ============================================================================

  const getActiveFilters = useCallback((): ExportFilter[] => {
    const filters: ExportFilter[] = [];
    if (recordTypeFilter) filters.push({ label: 'Record Type', value: getRecordTypeLabel(recordTypeFilter) });
    if (actionFilter) filters.push({ label: 'Action', value: actionFilter.toUpperCase() });
    if (dateFrom) filters.push({ label: 'Date From', value: dateFrom });
    if (dateTo) filters.push({ label: 'Date To', value: dateTo });
    if (searchQuery) filters.push({ label: 'Search', value: searchQuery });
    return filters;
  }, [recordTypeFilter, actionFilter, dateFrom, dateTo, searchQuery]);

  const handleExport = useCallback(() => {
    try {
      const filters = getActiveFilters();
      if (activeTab === 'approval') {
        if (filteredApprovalData.length === 0) { showMessage('No data to export.', 'info'); return; }
        const NUMERIC = ['Amount'];
        const rows = filteredApprovalData.map((row) => ({
          'Reference #': row.referenceNumber,
          'Record Type': getRecordTypeLabel(row.recordType),
          'Customer': row.customerName || '',
          'Amount': row.amount ?? '',
          'Action': row.action?.toUpperCase() || '',
          'Actioned By': row.actionedByName || '',
          'Remark': row.remark || '',
          'Actioned At': row.actionedAt ? formatDate(row.actionedAt) : '',
        }));
        exportStyledExcel('Approval History Report', 'Approval History', rows, 'approval-history-report', NUMERIC, filters);
        showMessage(`Exported ${rows.length} approval history record${rows.length !== 1 ? 's' : ''}.`, 'success');
      } else {
        if (filteredDataChangeData.length === 0) { showMessage('No data to export.', 'info'); return; }
        const rows = filteredDataChangeData.map((row) => ({
          'Record Type': getRecordTypeLabel(row.recordType),
          'Field': formatFieldName(row.fieldName),
          'Old Value': row.oldValue || '',
          'New Value': row.newValue || '',
          'Reason': row.reason || '',
          'Changed By': row.changedByName || '',
          'Changed At': row.changedAt ? formatDate(row.changedAt) : '',
        }));
        exportStyledExcel('Data Changes Report', 'Data Changes', rows, 'data-changes-report', [], filters);
        showMessage(`Exported ${rows.length} data change record${rows.length !== 1 ? 's' : ''}.`, 'success');
      }
    } catch {
      showMessage('Failed to export report.', 'error');
    }
  }, [activeTab, filteredApprovalData, filteredDataChangeData, showMessage, getActiveFilters]);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const isLoading = activeTab === 'approval' ? approvalLoading : dataChangeLoading;
  const hasError = activeTab === 'approval' ? approvalError : dataChangeError;
  const refetch = activeTab === 'approval' ? refetchApproval : refetchDataChange;

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', variant: 'icon', tooltip: 'Refresh',
      onClick: () => { refetchApproval(); refetchDataChange(); },
    },
    {
      id: 'export', icon: <IconSystem.download size={16} />, label: '', variant: 'icon', tooltip: 'Export to Excel',
      onClick: handleExport,
    },
  ];

  // Tab styles
  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '8px 20px',
    fontSize: '0.82rem',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
    borderBottom: isActive ? '2px solid var(--primary)' : '2px solid transparent',
    background: 'none',
    border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: isActive ? 'var(--primary)' : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  // Action filter options differ by tab
  const actionFilterOptions = activeTab === 'approval'
    ? [
        { value: '', label: 'All Actions' },
        { value: 'approved', label: 'Approved' },
        { value: 'returned', label: 'Returned' },
        { value: 'rejected', label: 'Rejected' },
      ]
    : [
        { value: '', label: 'All Actions' },
        { value: 'amended', label: 'Amended' },
        { value: 'created', label: 'Created' },
        { value: 'updated', label: 'Updated' },
      ];

  const recordTypeOptions = activeTab === 'approval'
    ? [
        { value: '', label: 'All Types' },
        { value: 'addon_request', label: 'Add-On Request' },
        { value: 'closing', label: 'Closing' },
        { value: 'combined', label: 'Combined' },
      ]
    : [
        { value: '', label: 'All Types' },
        { value: 'loan_mapping', label: 'Loan Mapping' },
        { value: 'addon_request', label: 'Add-On Request' },
        { value: 'closing', label: 'Closing' },
        { value: 'combined', label: 'Combined' },
      ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Audit Trail Report"
        icon={<IconSystem.lock size={20} />}
        description="Complete audit trail of approval actions and data changes."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search audit trail...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content">
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
            <button style={tabStyle(activeTab === 'approval')} onClick={() => { setActiveTab('approval'); setActionFilter(''); setRecordTypeFilter(''); }}>
              Approval History
            </button>
            <button style={tabStyle(activeTab === 'data')} onClick={() => { setActiveTab('data'); setActionFilter(''); setRecordTypeFilter(''); }}>
              Data Changes
            </button>
          </div>

          {/* Error state (non-blocking) */}
          {hasError && (activeTab === 'approval' ? filteredApprovalData : filteredDataChangeData).length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load report data</div>
                <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
              </div>
            </div>
          )}

          {/* Loading or content */}
          {isLoading && (activeTab === 'approval' ? filteredApprovalData : filteredDataChangeData).length === 0 ? (
            <GridLoadingSkeleton columns={6} />
          ) : (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {activeTab === 'approval' ? (
                <SyutGrid
                  data={filteredApprovalData}
                  columns={approvalColumns}
                  enableRowSelection={false}
                  enablePagination
                  enableColumnFilters={false}
                  emptyMessage="No approval history entries match the current filters."
                />
              ) : (
                <SyutGrid
                  data={filteredDataChangeData}
                  columns={dataChangeColumns}
                  enableRowSelection={false}
                  enablePagination
                  enableColumnFilters={false}
                  emptyMessage="No data change entries match the current filters."
                />
              )}
            </div>
          )}
        </div>

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Record Type"
                options={recordTypeOptions}
                value={recordTypeFilter}
                onChange={(val: string | string[] | null) => setRecordTypeFilter((val as string) || '')}
                placeholder="Search type..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>

            {activeTab === 'approval' && (
              <div className="filter-section">
                <SyutSelect
                  label="Action"
                  options={actionFilterOptions}
                  value={actionFilter}
                  onChange={(val: string | string[] | null) => setActionFilter((val as string) || '')}
                  placeholder="Search action..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>
            )}

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setRecordTypeFilter(''); setActionFilter(''); setDateFrom(''); setDateTo(''); }}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailReportScreen;
